import { Component, ChangeDetectionStrategy, input, output, signal, inject, computed, effect } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faXmark, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { UserDTO, GUEST_USER } from '../../domain/user/user.model';
import { authLogout } from '../../core/auth/auth.actions';
import { openDialog } from '../dialog/dn-dialog.actions';
import { SigninDialogComponent } from '../dialog/signin-dialog/signin-dialog.component';

interface UserMenuItem {
  label: string;
  link?: string;
  action?: string;
  expandable?: boolean;
  expanded?: boolean;
  children?: UserMenuItem[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-user-menu-panel',
  templateUrl: './user-menu-panel.component.html',
  styleUrls: ['./user-menu-panel.component.scss'],
  imports: [
    TranslatePipe,
    FaIconComponent,
  ],
})
export class UserMenuPanelComponent {
  // FontAwesome icons
  readonly faXmark = faXmark;
  readonly faChevronDown = faChevronDown;
  readonly faChevronUp = faChevronUp;

  // Input variables
  readonly user = input<UserDTO | undefined>(undefined);
  readonly isOpen = input<boolean>(false);

  // Output events
  readonly closePanel = output<void>();

  // State
  private store = inject(Store);
  private router = inject(Router);

  // Check if user is logged in
  isLoggedIn = signal<boolean>(false);

  constructor() {
    effect(() => {
      const currentUser = this.user();
      this.isLoggedIn.set(currentUser?.idAnagrafica !== GUEST_USER.idAnagrafica);
    });
  }

  // Menu items with expansion state - changes based on login status
  menuItems = computed<UserMenuItem[]>(() => {
    const loggedIn = this.isLoggedIn();
    const baseItems: UserMenuItem[] = loggedIn
      ? [{ label: 'dottnet.usermenu.profile', link: '/profile/edit' }]
      : [{ label: 'dottnet.usermenu.login', action: 'login' }];

    return [
      ...baseItems,
      {
        label: 'dottnet.usermenu.extra',
        expandable: true,
        expanded: false,
        children: [
          { label: 'dottnet.usermenu.discoverextra', link: '/extra' },
          { label: 'dottnet.usermenu.myextra', link: '/extra/my-extra' },
          { label: 'dottnet.usermenu.mycourses', link: '/fad' },
        ]
      },
      { label: 'dottnet.usermenu.managelog', link: '/log' },
      ...(loggedIn
        ? [{
            label: 'dottnet.menu.toolbox',
            expandable: true,
            expanded: false,
            children: [
              { label: 'dottnet.menu.pec', link: '/pec' },
              { label: 'dottnet.menu.mail', link: '/mail' },
            ]
          }]
        : []),
    ];
  });

  // Internal signal to track expanded state
  private expandedIndex = signal<number | null>(null);

  onCloseClick(): void {
    this.closePanel.emit();
  }

  onMenuItemClick(item: UserMenuItem, index: number): void {
    if (item.expandable) {
      this.toggleMenuItem(index);
    } else if (item.action === 'login') {
      this.openSigninDialog();
    } else if (item.link) {
      this.closePanel.emit();
      this.router.navigate([item.link]);
    }
  }

  onChildItemClick(child: UserMenuItem, event: Event): void {
    event.stopPropagation();
    if (child.link) {
      this.closePanel.emit();
      this.router.navigate([child.link]);
    }
  }

  onMenuItemKeydown(event: KeyboardEvent, item: UserMenuItem, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onMenuItemClick(item, index);
    }
  }

  onChildItemKeydown(event: KeyboardEvent, child: UserMenuItem): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (child.link) {
        this.closePanel.emit();
        this.router.navigate([child.link]);
      }
    }
  }

  toggleMenuItem(index: number): void {
    const currentExpanded = this.expandedIndex();
    this.expandedIndex.set(currentExpanded === index ? null : index);
  }

  isExpanded(index: number): boolean {
    return this.expandedIndex() === index;
  }

  openSigninDialog(): void {
    this.closePanel.emit();
    this.store.dispatch(
      openDialog({
        content: undefined,
        urlToDownload: undefined,
        urlWithContext: undefined,
        componentType: SigninDialogComponent,
        componentName: 'SigninDialogComponent',
        panelClass: 'dn-dialog-small',
      })
    );
  }

  getUserInitials(): string {
    const user = this.user();
    if (!user || user.nome === 'Guest' || (!user.nome && !user.cognome)) {
      return '';
    }
    const firstInitial = user.nome ? user.nome.charAt(0).toUpperCase() : '';
    const lastInitial = user.cognome ? user.cognome.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  }

  getUserFullName(): string {
    const user = this.user();
    if (!user || user.nome === 'Guest') {
      return '';
    }
    return `${user.categoria || 'Dott.'}\n${user.nome} ${user.cognome}`;
  }

  getUserEmail(): string {
    const user = this.user();
    return user?.email || '';
  }

  onLogoutClick(): void {
    this.store.dispatch(authLogout());
    this.closePanel.emit();
  }
}
